import * as XLSX from "xlsx";
import JSZip from "jszip";

const groupClassResultsforExport = (classesResults) =>
  Object.entries(classesResults).reduce((acc, [classId, classObj]) => {
    acc[classId] = {
      name: classObj.name,
      students: {},
    };

    Object.entries(classObj.students).forEach(([studentId, student]) => {
      if (!student.results || student.results.length === 0) {
        acc[classId].students[studentId] = student;
        return;
      }

      acc[classId].students[studentId] = {
        ...student,
        results: student.results.map((result) => {
          const groupedTasks = {};

          result.tasks.forEach((task) => {
            const groupKey = task.parentTaskid
              ? `group-${task.parentTaskid}`
              : `task-${task.taskid}`;

            if (!groupedTasks[groupKey]) {
              groupedTasks[groupKey] = {
                name: task.parentTaskName || task.name,
                tasks: [],
                aggregated: {
                  rawScore: 0,
                  standardScore: 0,
                  count: 0,
                },
              };
            }

            groupedTasks[groupKey].tasks.push(task);
            groupedTasks[groupKey].aggregated.rawScore += task.score || 0;
            groupedTasks[groupKey].aggregated.standardScore += task.standardScore || 0;
            groupedTasks[groupKey].aggregated.count++;
          });

          Object.values(groupedTasks).forEach((group) => {
            if (group.aggregated.count > 0) {
              group.aggregated.rawScore = Math.round(
                (group.aggregated.rawScore / group.aggregated.count) * 100
              );
              group.aggregated.standardScore = Math.round(
                group.aggregated.standardScore / group.aggregated.count
              );
            }
          });

          return {
            ...result,
            groupedTasks: Object.values(groupedTasks),
            originalTasks: result.tasks,
          };
        }),
      };
    });

    return acc;
  }, {});

export const ALADDIN_IMPORT_PDF_URL =
  "https://www.alpaca-assessment.com/hubfs/Aladdin%20Guides/How%20to%20Import%20ALPACA%20Results%20into%20Aladdin%20and%20Map%20Unmapped%20Students.pdf";
export const ALADDIN_IMPORT_VIDEO_URL = "https://www.youtube.com/watch?v=K9vgbw9w6bI";
export const ALADDIN_IMPORT_VIDEO_EMBED_URL = "https://www.youtube.com/embed/K9vgbw9w6bI";
export const ALADDIN_IMPORT_PDF_FILENAME = "Aladdin_Import_Guide_ALPACA.pdf";

const pad = (value) => String(value).padStart(2, "0");

export const getCurrentAcademicYear = (date = new Date()) => {
  const year = date.getFullYear();
  const boundary = new Date(Date.UTC(year, 7, 15));
  const comparable = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const startYear = comparable >= boundary ? year : year - 1;
  const endYear = startYear + 1;

  return {
    key: `${String(startYear).slice(-2)}/${String(endYear).slice(-2)}`,
    start: `${startYear}-08-15`,
    end: `${endYear}-08-15`
  };
};

export const formatAladdinDate = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${String(date.getUTCFullYear()).slice(-2)}`;
};

const safeFilePart = (value, fallback) =>
  String(value || fallback || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "") || fallback;

export const buildAladdinZipFileName = ({
  rollNumber,
  schoolName,
  assessmentName,
  academicYearKey,
  date = new Date()
}) => {
  const formattedDate = formatAladdinDate(date).replace(/\//g, "-");
  const yearPart = academicYearKey ? `_${safeFilePart(academicYearKey, "Year")}` : "";

  return `${safeFilePart(rollNumber, "rollNumber")}_${safeFilePart(schoolName, "School")}_${safeFilePart(
    assessmentName,
    "Assessment"
  )}${yearPart}_${formattedDate}.zip`;
};

const getStudentResults = (student) => (Array.isArray(student?.results) ? student.results : []);
const getGroupedTasks = (assessment) => (Array.isArray(assessment?.groupedTasks) ? assessment.groupedTasks : []);

export const buildAladdinClassRows = (classData = {}) => {
  const taskNames = new Set();

  Object.values(classData.students || {}).forEach((student) => {
    getStudentResults(student).forEach((assessment) => {
      getGroupedTasks(assessment).forEach((taskGroup) => {
        if (taskGroup?.name) taskNames.add(taskGroup.name);
      });
    });
  });

  const taskColumns = Array.from(taskNames);
  const rows = Object.values(classData.students || {}).map((student) => {
    const firstResult = getStudentResults(student)[0];
    let totalScore = 0;
    let taskCount = 0;

    getStudentResults(student).forEach((assessment) => {
      getGroupedTasks(assessment).forEach((taskGroup) => {
        if (taskGroup?.aggregated?.rawScore !== null && taskGroup?.aggregated?.rawScore !== undefined) {
          totalScore += taskGroup.aggregated.rawScore;
          taskCount += 1;
        }
      });
    });

    const row = {
      "Student First and Last Name": String(student?.name || "").trim(),
      "Test Date (D/M/Y)": formatAladdinDate(firstResult?.date),
      "Overall Score vs Test": taskCount > 0 ? Math.round(totalScore / taskCount) : ""
    };

    getStudentResults(student).forEach((assessment) => {
      getGroupedTasks(assessment).forEach((taskGroup) => {
        row[`Raw Score (${taskGroup.name})`] = taskGroup?.aggregated?.rawScore ?? "";
        row[`Standard Score (${taskGroup.name})`] = taskGroup?.aggregated?.standardScore ?? "";
      });
    });

    return row;
  });

  return {
    rows,
    headers: [
      "Student First and Last Name",
      "Test Date (D/M/Y)",
      "Overall Score vs Test",
      ...taskColumns.flatMap((taskName) => [`Raw Score (${taskName})`, `Standard Score (${taskName})`])
    ]
  };
};

export const addAladdinPdfToZip = async (zip, pdfUrl = ALADDIN_IMPORT_PDF_URL) => {
  try {
    if (typeof fetch !== "function") return zip;

    const response = await fetch(pdfUrl);
    const pdfBlob = await response.blob();
    zip.file(ALADDIN_IMPORT_PDF_FILENAME, pdfBlob);
  } catch (error) {
    console.error("Error adding Aladdin PDF guide to ZIP:", error);
  }

  return zip;
};

export const createAladdinResultsZip = async ({
  rawResults,
  selectedClassIds,
  assessmentName,
  rollNumber,
  includePdf = true
}) => {
  const groupedResults = groupClassResultsforExport(rawResults || {});
  const zip = new JSZip();
  const classNames = [];

  selectedClassIds.forEach((classId) => {
    const classData = groupedResults[classId];
    if (!classData || !classData.students) return;

    const { rows, headers } = buildAladdinClassRows(classData);
    if (rows.length < 1) return;

    const className = classData.name || `Class_${classId}`;
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Results");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    classNames.push(className);
    zip.file(
      `${safeFilePart(rollNumber, "rollNumber")}_${safeFilePart(className, `Class_${classId}`)}_${safeFilePart(
        assessmentName,
        "Assessment"
      )}.xlsx`,
      excelBuffer
    );
  });

  if (includePdf) {
    await addAladdinPdfToZip(zip);
  }

  return {
    zip,
    groupedResults,
    classNames
  };
};

export const buildAladdinEmailPayload = ({
  groupedResults,
  selectedClassIds,
  assessmentName,
  schoolName,
  rollNumber,
  academicYearKey
}) => ({
  assessmentName,
  schoolName,
  rollNumber,
  selectedYear: academicYearKey,
  selectedClasses: selectedClassIds.map((classId) => ({
    classId,
    className: groupedResults?.[classId]?.name || `Class_${classId}`,
    students: groupedResults?.[classId]?.students || {}
  }))
});
